import prependChildAtIndex from '../../../general/prepend/prependChildAtIndex';

import exLanguage from '../../../language';
import * as exInput from '../../../forms/input';
import * as exSelect from '../../../forms/select';
import * as exElement from '../../../utils/element';
import * as exHighlight from '../../../utils/highlight';
import * as exIcon from '../../../utils/icon';

import type { Context } from '../types';

// ----------------------------------------------------------------------------

function search(ctx: Context): any {
    ctx.shared.elements.search = {};
    ctx.shared.state.search = {};

    function init(): void {
        // If not using search and add option
        if (ctx.shared.config.isSearch !== true && ctx.shared.config.isAddOption !== true) {
            return;
        }

        //
        create();
        events();
    }

    function create(): void {
        // Create element (select search)
        const elSelectSearch = exElement.create({
            merge: ctx.shared.config.search,
            class: {
                new: 'select-search',
            },
        });
        prependChildAtIndex(ctx.shared.elements.dropdown.element, elSelectSearch, 0);
        ctx.shared.elements.search.element = elSelectSearch;

        // ----------------------------------------------------------------

        // Create element (search text)
        const elSearchText = exElement.create({
            class: {
                new: 'search-text',
            },
        });
        elSelectSearch.appendChild(elSearchText);
        ctx.shared.elements.search.text = elSearchText;

        // Create element (search input)
        const searchInput = exInput.create({
            type: 'search',
            attr: {
                autocomplete: 'off',
            },
            class: {
                new: 'form-control input-control',
            },
            placeholder: ctx.shared.config.placeholder ?? (exLanguage.line('select.search_here') as string),
        });
        elSearchText.appendChild(searchInput.element);
        ctx.shared.elements.search.input = searchInput.element;

        // ----------------------------------------------------------------

        // If not using is add option
        if (ctx.shared.config.isAddOption !== true) {
            // Create element (icon)
            const elIcon = exIcon.create({
                class: {
                    new: 'search-icon',
                    add: 'fa-solid fa-times',
                },
                attr: {
                    title: exLanguage.line('select.search_clear'),
                },
            });
            elSearchText.appendChild(elIcon);
            ctx.shared.elements.addOption.icon = elIcon;

            // Create event (click)
            elIcon.addEventListener('click', function (): any {
                // Clear input (search)
                clearValue();

                // Hide element (icon)
                this.hideElement();

                // Progress (search)
                progress();
            });
        }

        // ----------------------------------------------------------------

        // Create element (search result)
        const elSearchResult = exElement.create({
            class: {
                new: 'search-result',
            },
            style: {
                display: 'none',
            },
            html: ctx.shared.config.fnResult ?? exLanguage.line('select.search_no_found'),
        });
        elSelectSearch.appendChild(elSearchResult);
        ctx.shared.elements.search.result = elSearchResult;
    }

    function events(): void {
        // Create event (click)
        ctx.shared.elements.search.element.addEventListener('click', function (evt): any {
            evt.stopPropagation();
        });

        // Create event (input)
        ctx.shared.elements.search.input.addEventListener('input', function (this): any {
            progress();
            ctx.addOption.progress();

            // If there is element (search icon)
            if (ctx.shared.elements.addOption.icon) {
                if (this.value) {
                    ctx.addOption.showElement();
                } else {
                    ctx.addOption.hideElement();
                }
            }
        });
    }

    // ------------------------------------------------------------------------

    function progress(): void {
        // If not using search and add option
        if (ctx.shared.config.isSearch !== true && ctx.shared.config.isAddOption !== true) {
            return;
        }

        // --------------------------------------------------------------------

        // Get element (search input)
        const elSearchInput = ctx.search.getInputElement();

        // Search Options
        const listOptions = exSelect.searchOptions(ctx.current.getElement(), elSearchInput.value);
        //
        if (!listOptions) {
            return;
        }

        // Set search result
        ctx.search.setResultValue(listOptions.found);

        // --------------------------------------------------------------------

        // Read
        ctx.read.reset();

        // --------------------------------------------------------------------

        // If using highlight
        if (ctx.shared.config.search?.isHighlight === true) {
            // Get element (select listing)
            const elSelectListing = ctx.listing.getElement();

            // Highlight Start
            exHighlight.start(elSelectListing, elSearchInput.value);
        }

        // --------------------------------------------------------------------

        // Get element (search result)
        const elSearchResult = getResultElement();

        // If there is result
        if (getResultValue().length) {
            exElement.hide(elSearchResult);
        }
        // If there is no result
        else {
            exElement.show(elSearchResult);
        }
    }

    // ------------------------------------------------------------------------

    function clear(): void {
        // If not using search or add option
        if (ctx.shared.config.isSearch !== true && ctx.shared.config.isAddOption !== true) {
            return;
        }

        // --------------------------------------------------------------------

        // Clear search input
        clearValue();

        // Hide element (search result)
        ctx.search.getResultElement().hideElement();

        // Clear search result
        ctx.search.clearResultValue();

        // --------------------------------------------------------------------

        // If not using is add option
        if (ctx.shared.config.isAddOption !== true) {
            // Hide element (search icon)
            ctx.addOption.hideElement();
        }
    }

    // ------------------------------------------------------------------------

    function getElement(): HTMLElement {
        return ctx.shared.elements.search.element;
    }

    function getInputElement(): HTMLInputElement {
        return ctx.shared.elements.search.input;
    }

    function getInputValue(): string {
        return getInputElement().value;
    }

    function getResultElement(): HTMLElement {
        return ctx.shared.elements.search.result;
    }

    function getResultValue(): any {
        return ctx.shared.state.search.result;
    }

    function setValue(value: string): void {
        getInputElement().value = value;
    }

    function setResultValue(value: string): void {
        ctx.shared.state.search.result = value;
    }

    function clearValue(): void {
        getInputElement().value = '';
    }

    function clearResultValue(): void {
        ctx.shared.state.search.result = null;
    }

    // ------------------------------------------------------------------------

    return {
        init,
        progress,
        clear,
        getElement,
        getInputElement,
        getInputValue,
        getResultElement,
        getResultValue,
        setValue,
        setResultValue,
        clearValue,
        clearResultValue,
    };
}

// ----------------------------------------------------------------------------

export default search;
